import { ChangeDetectionStrategy, Component, DestroyRef, HostListener, Inject, OnInit, PLATFORM_ID, ViewChild, WritableSignal, afterNextRender, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { SignalsStoreService } from '../signals-store.service';
import { AuthService } from '../../authentication/auth.service';
import { LocalStorageService } from '../local-storage.service';
import { DeliveriesService } from '../../settings/account/deliveries/deliveries.service';
import { ModalContentService } from '../modal-content/modal-content.service';
import { ModalContentTypes } from '../constants/modal-content-types';
import { FormsModule } from '@angular/forms';
import { ModalContentData } from '../modal-content/modal-content';
import { isPlatformBrowser, CommonModule, Location } from '@angular/common';
import { environment } from '../../../environments/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, tap } from 'rxjs';
import { SearchBarComponent } from './search-bar/search-bar.component';
import { HeaderService } from './header.service';
import { MenuService } from '../menu.service';
import { ProductsService } from '../../product/products.service';
import { FIREBASE_COLLECTIONS, LOCALSTORAGE_KEYS } from '../constants/databases';
import { Session } from '../types/session.type';
import { NgbModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { OrderService } from '../order.service';
import { SubscriptionsService } from '../../settings/account/subscriptions/subscriptions.service';
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatOption } from "@angular/material/autocomplete";
import { MatSelect } from "@angular/material/select";
import { ResolutionService } from '../resolution.service';
import { NAVIGATION_ITEMS, RIGHT_ELEMENTS, SETTINGS_PAGES } from './header.lists';
import { StockService } from '../../stock/stock.service';
import { AccountService } from '../account.service';
import { eCommercePermissions } from '../types/account.types';
import { formatDateToReadableString } from '../utils/formatting';
import { KlaviyoService } from '../klaviyo/klaviyo.service';
import { KLAVIYOEVENTS } from '../klaviyo/events';
import { TopBarComponent } from "./top-bar/top-bar.component";
import { formatStringForURL, isAddressUpdateRequired } from '../common/utils';
import { UserPreferences } from '../types/common.types';
import { DateTime } from 'luxon';
import { FirebaseCrudService } from '../firebase-crud.service';
import { PastDueService } from '../past-due.service';

@Component({
  selector: 'app-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    FormsModule,
    SearchBarComponent,
    CommonModule,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    RouterLinkActive,
    NgbModule,
    TopBarComponent,
  ],
  providers: [SubscriptionsService],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit {
  signalsStoreService = inject(SignalsStoreService);
  modalContentService = inject(ModalContentService);
  authService = inject(AuthService);
  HAS_MEMBERSHIP_FLOW: boolean = !!environment.config.flows.membership;

  private localStorageService = inject(LocalStorageService);
  private deliveriesService = inject(DeliveriesService);
  private router = inject(Router);
  private headerService = inject(HeaderService);
  private menuService = inject(MenuService);
  private productService = inject(ProductsService);
  private activeModal = inject(NgbModal);
  private orderService = inject(OrderService);
  private location = inject(Location);
  private subscriptionsService = inject(SubscriptionsService);
  private resolutionService = inject(ResolutionService);
  private stockService = inject(StockService);
  private productsService = inject(ProductsService);
  private accountService = inject(AccountService);
  private klaviyoService = inject(KlaviyoService);
  private firebaseCrudService = inject(FirebaseCrudService);
  private destroyRef = inject(DestroyRef);
  #pastDueService = inject(PastDueService);

  orderProductsQuantity = computed(() => this.getOrderProductsQuantity());
  isSessionLoaded = computed(() => this.signalsStoreService.isSessionLoaded());
  shouldShowCategoriesBar = computed(() => this.signalsStoreService.shouldShowCategoriesBar());
  isCustomBoxSignupFlow = computed(() => this.signalsStoreService.isCustomBoxSignupFlow());

  routeSignal = signal(this.router.url);

  isShowingSearchBar = computed(() => !!(this.headerService.displaySearchBar() || this.signalsStoreService.filterByProducer()));
  isSidebarOpen = this.signalsStoreService.isSidebarOpen;

  isShowingSearchMobile = computed(() => (!!(this.headerService.displaySearchBar() || this.signalsStoreService.filterByProducer()) && this.isMobile()));

  userName: WritableSignal<string> = signal('');

  menuItems = this.menuService.menu;

  isMobile = computed(() => this.resolutionService.isMobile());

  landingPages = signal(NAVIGATION_ITEMS);
  settingsPages = computed(() => this.setUpSettingsPermissions());
  rightElements = computed(() => this.setUpRightElementPermissions());

  isShowingMobileFooter = this.signalsStoreService.isShowingMobileFooter;

  logoVersionNumber = computed(() => this.signalsStoreService.logoVersionNumber());

  isContentLoading = computed(() => this.stockService.isLoading() || this.productsService.isLoading());
  isContentLoaded = computed(() => this.stockService.isLoaded() || this.productsService.isLoaded());

  session = computed(() => this.signalsStoreService.sessionSignal());

  // The auth-dependent menu depends on the session read from localStorage, which
  // the server can't see: SSR renders the logged-out branch (Log In / Join) while
  // the client reads the session synchronously at bootstrap and renders the
  // logged-in branch, leaving the server DOM orphaned next to the client DOM
  // (both branches showing at once in the mobile menu). Gate those branches on
  // `hydrated`, flipped true in afterNextRender (browser-only, after the first
  // render), so server and initial client markup match and the real branch
  // appears only post-hydration.
  hydrated = signal(false);

  @ViewChild(MatSelect) matSelect!: MatSelect;

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    afterNextRender(() => this.hydrated.set(true));
  }

  getOrderProductsQuantity() {
    const orders = this.orderService.orders();
    if (!orders.length) return 0;
    const order = orders[0] || [];
    const allProducts = [
      ...(order?.products?.common ?? []),
      ...(order?.products?.subscription ?? []),
    ];

    if (order?.isSkipped) return 0;

    return new Set(allProducts.map((product: any) => {
      const variantId = product.variant.id;
      const packageId = product.package?.id || null;
      const id = `${variantId}${packageId ? `_${packageId}` : ''}`;
      return id
    })).size;
  }

  logOut() {
    this.authService.logout();
  }

  ngOnInit(): void {
    if (this.signalsStoreService.filterByProducer()) {
      this.headerService.displaySearchBar.set(true);
    }
    this.subscribeToRouteChanges();
    this.setUpMenu();
    this.signalsStoreService.getAndSyncCart();
    this.setUpUserSession();
    this.getSubscriptions();
  }

  private setUpMenu() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.menuService.fetchMenuCategories().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.setUpSelectedCategory(this.location.path()),
    });
  }

  private setUpUserSession() {
    const session: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION) || null;
    const permissions: eCommercePermissions | null = this.localStorageService.get(LOCALSTORAGE_KEYS.PERMISSIONS);
    if (session?.accountInfo?.id) {
      this.#getUserPreferences(session?.accountInfo?.id);
      this.deliveriesService.getDeliveryDates();
      this.accountService.getMembershipPermissions().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
      this.headerService.getFrequencies();
      this.userName.set(session.accountInfo.firstName.toUpperCase());
      if (
        !(!!(permissions?.settings.paymentMethod.allowed) && !(session?.settings?.hasPaymentMethod)) &&
        !(!!(session?.settings?.requireAddress) && !(session?.address?.street))
      ) {
        this.getOrder();
      }
      this.deliveriesService.getDeliveryZoneInfo().pipe(tap(() => this.validateNewUserOrFirstLogin()), takeUntilDestroyed(this.destroyRef)).subscribe();
    }
  }

  toggleSearchBar() {
    // this.isShowingSearchBar.update((current: boolean) => !current);
    this.headerService.updateSearchBarDisplay();
  }

  toggleSearchMobile() {
    this.headerService.updateSearchBarDisplay();
    if (this.signalsStoreService.isFiltering()) this.headerService.clearFilters(true);
  }

  toggleCategorySideMenu() {
    this.signalsStoreService.isSidebarOpen.update(
      (currentValue) => !currentValue
    );
  }

  private subscribeToRouteChanges() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe((event: any) => {
        this.routeSignal.set(this.router.url);
        this.setUpSelectedCategory(event.url);
      });
  }

  private getOrder() {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.signalsStoreService.hasSession()) return;
    const currentUrl = this.location.path();
    if (((!currentUrl.includes('/order') || currentUrl.includes('/order-and-billing')) && !currentUrl.includes('/cart')) || this.signalsStoreService.isLimitedUser())
      this.orderService.getOrders(this.signalsStoreService.marketStatus, false);
  }

  openModalWelcomeMessage() {
    const sessionData = (): any => {
      const hasSession = this.signalsStoreService.hasSession();
      const userData: Session | null = hasSession ? this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION) : null;
      const formattedDeliveryDate = userData?.deliveryInfo?.deliveryDate ? formatDateToReadableString(userData?.deliveryInfo?.deliveryDate).readableString : '';
      const deliveryInfo = { ...userData?.deliveryInfo, formattedDeliveryDate };
      return {
        title: `Welcome to Locavore`,
        hasSession,
        fullScreenOnMobile: true,
        welcomeMessage: {
          ...deliveryInfo,
          isPickUp: !!userData?.pickUpInfo
        },
      } as ModalContentData;
    };
    const data: ModalContentData = sessionData();
    this.modalContentService.openModal(ModalContentTypes.WELCOME_MESSAGE, data);
  }

  getCategoryUrl(categoryName: string): string {
    return formatStringForURL(categoryName);
  }

  triggerCategoryInfoFetch(
    category: { id: number; name: string },
    navigate: boolean = true
  ) {
    if (this.isContentLoading()) return;
    const formattedCategoryName = formatStringForURL(category.name);
    const parts = this.location.path().split('/');
    const currentCategoryName = parts[parts.length - 1].toLowerCase();
    if (formattedCategoryName === currentCategoryName && navigate && parts.length === 3) return;
    navigate ? this.router.navigate([`/shop/${formattedCategoryName}`]) : null;
    this.signalsStoreService.selectedCategory.set(category);
    this.localStorageService.set(LOCALSTORAGE_KEYS.SELECTED_CATEGORY, category);
  }

  private validateNewUserOrFirstLogin() {
    isAddressUpdateRequired(this.localStorageService, this.modalContentService, this.activeModal, this.router);
    // A past-due warning outranks the welcome message: the customer needs to
    // know their deliveries are at risk before anything else competes for the
    // screen.
    //
    // The flag it reads is a login-time snapshot, so it is checked against the
    // live past-due invoices before anything opens (DEV-10988). Customers who
    // are not flagged get a synchronous answer here, which is why the welcome
    // message is not waiting on a round trip.
    this.#pastDueService.revalidate().subscribe(isAtRisk => {
      if (isAtRisk && this.validatePastDueModal()) return;
      this.validateWelcomeMessage();
    });
  }

  /** The welcome message queues behind the past-due warning. */
  private validateWelcomeMessage() {
    const newUser = this.localStorageService.get(LOCALSTORAGE_KEYS.NEW_ACCOUNT);
    const firstLogin = this.localStorageService.get(LOCALSTORAGE_KEYS.FIRST_LOGIN);
    if (newUser || firstLogin) {
      this.openModalWelcomeMessage();
      this.localStorageService.remove(LOCALSTORAGE_KEYS.NEW_ACCOUNT);
      this.localStorageService.remove(LOCALSTORAGE_KEYS.FIRST_LOGIN);
    }
  }

  /**
   * Past-due warning (DEV-10295). Shown once per browser session so a customer
   * who dismisses it is not nagged on every navigation, and never for suspended
   * accounts, which have their own screen.
   *
   * Returns true only when it actually opens the modal in this load, so the
   * caller knows whether the slot was taken.
   */
  private validatePastDueModal(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;

    const session = this.signalsStoreService.sessionSignal();
    const billing = session?.billing;

    if (!billing?.isAtRisk) return false;
    if (session?.settings?.isSuspended) return false;

    const PAST_DUE_SHOWN_KEY = 'pastDueModalShown';
    if (sessionStorage.getItem(PAST_DUE_SHOWN_KEY)) return false;

    this.modalContentService.openModal(
      ModalContentTypes.ACCOUNT_PAST_DUE,
      {
        closeable: true,
        pastDue: {
          pastDueBalance: billing.pastDueBalance ?? 0,
          daysUntilSuspension: billing.daysUntilSuspension ?? null,
          invoiceHistoryPath: billing.invoiceHistoryPath ?? '/settings/billing/invoice-history',
        },
      },
    );

    sessionStorage.setItem(PAST_DUE_SHOWN_KEY, '1');

    return true;
  }

  private setUpSelectedCategory(url: string) {
    if (this.signalsStoreService.filterByProducer()) return;
    if (url.indexOf('/shop') > -1) {
      const parts = url.split('/');
      const shopIndex = parts.findIndex(e => e === 'shop');
      const categoryIndex = shopIndex === parts.length - 1
        ? shopIndex
        : shopIndex + 1;

      let categoryName = parts[categoryIndex].toLowerCase();
      // Validation for query params:
      if (categoryName.indexOf('?') > -1) categoryName = categoryName.split('?')[0];
      if (!this.menuItems().length) return;
      let category = undefined;
      if (categoryName === 'shop') {
        if (
          this.signalsStoreService.isFiltering() &&
          this.signalsStoreService.selectedCategory().id > -1
        ) {
          category = this.signalsStoreService.selectedCategory();
        } else {
          category = this.menuItems()[0];
        }
      } else
        category = this.menuItems().find(
          (e: any) => formatStringForURL(e.name) === categoryName
        );
      if (!category) return;
      this.triggerCategoryInfoFetch(category, categoryName === 'shop');
      if (this.signalsStoreService.isFiltering()) {
        // If the user is making a filter, the, avoid to get products and should get stock for those products:
        const ids = this.productService.productsSignal().map((p: any) => p.id);
        this.stockService.getStock(undefined, ids);
        return
      };
      this.productService.getProductsResume(category.id);
    } else {
      this.signalsStoreService.selectedCategory.set({ id: -1, name: '' });
    }
  }

  private getSubscriptions() {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.signalsStoreService.hasSession()) return;
    this.subscriptionsService.get();
  }

  private setUpSettingsPermissions() {
    const permissions = this.signalsStoreService.permissions();
    if (!permissions) return;

    // Set Up Settings Pages:
    const items = JSON.parse(JSON.stringify(SETTINGS_PAGES))
    const settingPages = items.map((p: { children: any[]; }) => {
      const children = p.children.filter((c: { permissionName: string; }) => {
        const keys = c.permissionName.split('.');
        let permission: any = permissions;
        for (const key of keys) {
          permission = permission[key]
        }
        return permission.allowed
      });
      p.children = children || [];
      return p
    }).filter((p: { children: string | any[]; }) => p.children?.length);

    return settingPages;
  }

  private setUpRightElementPermissions() {
    // Get the local storage permissions variable:
    const permissions: any = this.signalsStoreService.permissions();
    if (!permissions) return;

    // Set Up Right Elements:
    const items = JSON.parse(JSON.stringify(RIGHT_ELEMENTS));
    const rightElements = items.filter((p: { showWithoutPermission: any; permissionName: string; }) => {
      if (p.showWithoutPermission) return true;
      const keys = p.permissionName.split('.');
      let permission = permissions;
      for (const key of keys) {
        permission = permission[key]
      }
      return permission.allowed
    });

    return rightElements || []
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.matSelect?.panelOpen) return;
    const targetElement = event.target as HTMLElement;
    if (!this.matSelect._elementRef.nativeElement.contains(targetElement)) {
      this.matSelect.close();
    }
  }

  #getUserPreferences(userId: number) {
    this.firebaseCrudService.getById(FIREBASE_COLLECTIONS.PREFERENCES, userId.toString(), (result: UserPreferences) => {
      if (!result) {
        return this.signalsStoreService.userPreferences.set({
          dontShowAgainPrevDislike: {
            date: 0,
            value: false
          },
          holdUpSubscriptions: {
            date: 0,
            value: false
          },
          dontShowAgainAddedProduct: {
            value: false,
            date: 0
          }
        });
      }

      this.signalsStoreService.userPreferences.set(result);
      this.#validateExpiredUserPreferences(userId, result);
    });
  }

  #validateExpiredUserPreferences(userId: number, preferences: { [key: string]: { value: boolean, date: number } }) {

    let update = false;

    for (const key in preferences) {

      const { value, date } = preferences[key];

      if (!value)
        continue;

      const diffMonths = DateTime.utc().diff(DateTime.fromMillis(date), 'months').months;

      if (diffMonths >= 3) {
        preferences[key].value = false;
        preferences[key].date = 0;
        update = true;
      }
    }

    if (!update)
      return;

    this.firebaseCrudService.update(FIREBASE_COLLECTIONS.PREFERENCES, userId, preferences);
  }
}
